import { Fragment, useEffect, useState } from "react";
import { Box, Typography, Grid } from "@mui/material";
import { useAxios, useComponent } from "../../hooks";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader, LoadingButton } from "../../components";
import { getVisitorFaceData } from "../../utils/getVisitorFaceData";
import { useSelector } from "react-redux";

const EventSuccess = () => {
  const axios = useAxios();
  const navigate = useNavigate();
  const { businessData } = useSelector((state) => state.memberDetails);
  const { state, search } = useLocation();
  const { loader } = useComponent();
  const queryParams = new URLSearchParams(search);

  const entityID = queryParams.get("entity_id");
  const visitorID =
    queryParams.get("visitorId") || queryParams.get("visitor_id");
  const bookingID = queryParams.get("booking_id");
  const userTypeId = queryParams.get("user_type_id");

  const [premises, setPremisesData] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(state);
  const [entityDetails, setEntityDetails] = useState([]);
  const [timer, setTimer] = useState(5); // ⏳ 3-second timer

  useEffect(() => {
    axios({
      url: `/admin/profile/get-premises-banner?premises_id=` + entityID,
      disableRedirect: true,
      disableError: true,
    }).then((response) => {
      if (response.status) {
        setPremisesData(response.data);
      }
    });
  }, []);

  // Fetch booking details
  useEffect(() => {
    loader.start();
    axios({
      url: "admin/profile/get-visitor-booking-status",
      method: "POST",
      disableRedirect: true,
      disableError: true,
      data: {
        visitor_id: visitorID,
        entity_id: entityID,
        user_type_id: userTypeId,
        booking_id: bookingID,
        face_data: getVisitorFaceData(visitorID),
      },
    })
      .then((response) => {
        if (response.status) {
          setUserData({ userData: response.data });
        } else {
          setError("Failed to fetch booking details.");
        }
      })
      .catch(() => setError("Something went wrong!"))
      .finally(() => {
        loader.apiComplete();
        setLoading(false);
      });
  }, []);

  // Fetch event details
  useEffect(() => {
    axios({
      url: `admin/events/event-details?event_id=${entityID}`,
      method: "GET",
      customAuthToken: "9beaa95b-c59f-4ec9-bb60-5f4ee1986311",
    }).then((response) => {
      if (response.status) {
        setEntityDetails(response.data);
      }
    });
  }, [entityID]);

  // Timer countdown + auto function call
  useEffect(() => {
    if (!loading && timer > 0) {
      const countdown = setTimeout(() => setTimer((prev) => prev - 1), 1000);
      return () => clearTimeout(countdown);
    } else if (timer === 0 && userData?.userData?.[0]?.visitor_redirect_url) {
      // 👉 Automatically call the function after timer ends
      window.location.href = userData.userData[0].visitor_redirect_url;
    }
  }, [loading, timer, userData]);

  return (
    <Loader>
      <Box
        display="flex"
        justifyContent="center"
        alignItems="flex-start"
        sx={{
          background: "#ffffffff",
          fontFamily: "Fira Sans, Montserrat, sans-serif",
          py: { xs: 2, sm: 4 },
          overflowY: "auto",
        }}
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          textAlign="center"
          sx={{
            width: { xs: "92%", sm: "420px" },
            maxWidth: "420px",
            mx: "auto",
          }}
        >
          <Box
            component="img"
            src={
              premises.premises_banner ||
              entityDetails.banner_image ||
              businessData.logo
            }
            alt="Event Banner"
            sx={{
              mt: 2,
              mb: 3,
              width: "100%",
              maxWidth: "100%",
              borderRadius: 3,
              boxShadow: 2,
              height: "25vh",
              objectFit: entityDetails.banner_image ? "contain" : "cover",
              marginTop: 8,
            }}
          />

          {/* Title */}
          <Typography
            mt={4}
            variant="h4"
            fontWeight="bold"
            gutterBottom
            sx={{
              fontSize: { xs: "1.6rem", sm: "1.8rem" },
              textAlign: "center",
            }}
          >
            ✅ Verification Complete
          </Typography>

          <Typography
            variant="body1"
            fontWeight={500}
            sx={{
              fontSize: { xs: 14, sm: 16 },
              textAlign: "center",
              maxWidth: 450,
            }}
            mb={4}
          >
            Thank you! Your verification has been successfully completed.
          </Typography>

          {/* Timer Loader */}
          {userData?.userData?.[0]?.visitor_redirect_url && (
            <>
              <Typography variant="h6" color="text.secondary" mb={2}>
                Redirecting in {timer} seconds...
              </Typography>
            </>
          )}
        </Box>
      </Box>
    </Loader>
  );
};

export default EventSuccess;
